import React from "react";

import { Button, Divider, TextAreaField } from "@/components/ui";
import { FormField } from "@/components/ui/Form";
import type { ProgrammePlatformClient } from "@/platform";
import type { ReviewOutcome } from "@/server/types";

/** A reviewer's note and decisions on a change case in internal review. */
export function ChangeCaseReviewActions({
  client,
  programmeId,
  changeCaseId,
  reviewNote,
  setReviewNote,
  busy,
  isPending,
  run,
}: {
  client: ProgrammePlatformClient;
  programmeId: string;
  changeCaseId: string;
  reviewNote: string;
  setReviewNote: (note: string) => void;
  busy: boolean;
  isPending: (action: string) => boolean;
  run: (action: string, write: () => Promise<unknown>) => Promise<boolean>;
}): React.ReactElement {
  const refusal = (action: string, missing?: string): string | undefined =>
    isPending(action) ? "Wait for the change in progress to finish." : missing;

  // The note is emptied only once the review has landed, so a refusal keeps it.
  const review = async (key: string, outcome: ReviewOutcome, note?: string): Promise<void> => {
    const landed = await run(key, () =>
      client.reviewChangeCase({
        programmeId,
        changeCaseId,
        outcome,
        ...(note ? { note } : {}),
      }),
    );
    if (landed && note) {
      setReviewNote("");
    }
  };

  return (
    <div className="mt-4 grid gap-3">
      <Divider className="col-span-full" />
      <FormField label="Review note" htmlFor="change-case-review-note">
        <TextAreaField
          id="change-case-review-note"
          value={reviewNote}
          onChange={setReviewNote}
          disabled={busy}
          disabledReason="Wait for the change in progress to finish."
          data-testid="change-case-review-note"
        />
      </FormField>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          tone="danger"
          disabled={isPending("request-changes") || !reviewNote.trim()}
          disabledReason={refusal("request-changes", "Add a review note first.")}
          onClick={() => review("request-changes", "change-request", reviewNote.trim())}
          data-testid="request-change-case-changes"
        >
          Request changes
        </Button>
        <Button
          size="sm"
          variant="primary"
          disabled={isPending("approve-review")}
          disabledReason={refusal("approve-review")}
          onClick={() => review("approve-review", "approved", reviewNote.trim() || undefined)}
          data-testid="approve-change-case-review"
        >
          Approve review
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={isPending("mark-reviewed")}
          disabledReason={refusal("mark-reviewed")}
          onClick={() =>
            run("mark-reviewed", () => client.markChangeCaseReviewed({ programmeId, changeCaseId }))
          }
          data-testid="mark-change-case-reviewed"
        >
          Mark internal review complete
        </Button>
      </div>
    </div>
  );
}
